import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "../../../../redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";

const urlBase = "api/coverages";

type RequestParams = Record<string, any>;

export interface CoverageRCPayload {
  titulo: string;
  deducible: number | string;
  proveedor: number | string;
  moneda: number | string;
  from?: string;
  [key: string]: any;
}

export interface CoveragesRCState {
  data: any;
  suggestions: any[];
  defaultValues: CoverageRCPayload | null;
  currentPage: number;
  params: RequestParams;
  meta: any;
  total: number;
}

export const getCoveragesRC = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("coveragesRC/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(urlBase, { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getCoveragesRCSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("coveragesRC/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`${urlBase}/suggestions`, { params: query });
  return {
    data: response.data
  };
});

export const getCoverageRCById = createAsyncThunk<any, number>(
  "coveragesRC/getById",
  async (id: number) => {
    const response = await api.get(`${urlBase}/${id}`);
    return {
      data: response.data
    };
  }
);

export const createCoverageRC = createAsyncThunk<
  any,
  CoverageRCPayload,
  { dispatch: AppDispatch; state: RootState }
>("coveragesRC/create", async (payload, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const body = {
    producto: productSelected?.value,
    deducible: payload.deducible,
    proveedor: payload.proveedor,
    concepto: payload.titulo,
    moneda: payload.moneda,
    business_line: businessLineSelected?.id
  };

  const response = await api.post(urlBase, body);

  if (response.data.status === "success") {
    if (payload.from === "providers") {
      dispatch(getCoveragesRC({ format: "options", free: true }));
    }
    await dispatch(
      showModalSuccess(
        payload.from === "providers"
          ? { message: response.data.message }
          : { message: response.data.message, url: "goBack" }
      )
    );
  }

  return {
    data: response.data
  };
});

export const updateCoverageRC = createAsyncThunk<
  any,
  { id: number | string; data: CoverageRCPayload },
  { dispatch: AppDispatch; state: RootState }
>("coveragesRC/update", async ({ id, data }, { dispatch, getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const payload = {
    producto: productSelected?.value,
    deducible: data.deducible,
    proveedor: data.proveedor,
    concepto: data.titulo,
    moneda: data.moneda,
    business_line: businessLineSelected?.id
  };

  const response = await api.put(`${urlBase}/${id}`, payload);

  if (response.data.status === "success") {
    const url = "goBack";
    dispatch(showModalSuccess({ message: response.data.message, url }));
  }

  return {
    data: response.data
  };
});

export const deleteCoverageRC = createAsyncThunk<
  any,
  number,
  { dispatch: AppDispatch }
>("coveragesRC/delete", async (id, { dispatch }) => {
  const response = await api.delete(`${urlBase}/${id}`);

  if (response.data.status === "success") {
    dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getCoveragesRC());
  }

  return {
    data: response.data
  };
});

const initialState: CoveragesRCState = {
  data: [],
  suggestions: [],
  defaultValues: null,
  currentPage: 1,
  params: {},
  meta: {},
  total: 0
};

export const coveragesRCSlice = createSlice({
  name: "coveragesRC",
  initialState,
  reducers: {
    resetCoveragesRC: (state) => {
      state.data = [];
      state.suggestions = [];
      state.defaultValues = null;
      state.currentPage = 1;
      state.params = {};
      state.meta = {};
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCoveragesRC.fulfilled, (state, action) => {
      if (action.payload?.params?.format === "options") {
        state.suggestions = action.payload.data;
      } else {
        state.data = action.payload.data;
        state.currentPage = action.payload.data?.current_page;
        state.params = action.payload.params;
        state.meta = action.payload.data?.meta;
        state.total = action.payload.data?.meta?.total;
      }
    });
    builder.addCase(getCoverageRCById.fulfilled, (state, action) => {
      const payloadData = action.payload?.data?.data || {};
      state.defaultValues = {
        titulo: payloadData.concept,
        deducible: payloadData.deductible_percentage,
        proveedor: payloadData.proveedor ?? payloadData.provider_id ?? payloadData.provider?.id ?? "",
        moneda: payloadData.moneda ?? payloadData.currency_id ?? payloadData.currency?.id ?? ""
      };
    });
    builder.addCase(getCoveragesRCSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data;
    });
  }
});

export const { resetCoveragesRC } = coveragesRCSlice.actions;

export default coveragesRCSlice.reducer;
